import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import contentService from '../services/contentService';
import './InformeModal.css';

const InformeModal = ({ isOpen, onClose, availableInfluencers }) => {
  const [formData, setFormData] = useState({
    influencer: '',
    fecha_inicio: '',
    fecha_termino: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData({
        influencer: '',
        fecha_inicio: '',
        fecha_termino: ''
      });
      setError('');
    }
  }, [isOpen]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setError('');
  };

  const validateForm = () => {
    if (!formData.influencer) {
      setError('Por favor selecciona una cuenta');
      return false;
    }
    if (!formData.fecha_inicio) {
      setError('Por favor selecciona una fecha de inicio');
      return false;
    }
    if (!formData.fecha_termino) {
      setError('Por favor selecciona una fecha de término');
      return false;
    }
    if (new Date(formData.fecha_inicio) > new Date(formData.fecha_termino)) {
      setError('La fecha de inicio debe ser anterior a la fecha de término');
      return false;
    }
    return true;
  };

  const handleGenerarInforme = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Llamar al endpoint del backend para generar el informe
      console.log('Generando informe con:', formData);

      const response = await contentService.generateInforme(formData);

      // Crear un blob URL para descargar el archivo
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Obtener nombre del influencer para el archivo
      const influencerName = availableInfluencers.find(
        inf => inf.id === parseInt(formData.influencer)
      )?.name || 'Informe';

      link.setAttribute(
        'download',
        `Informe_Metricas_${influencerName}_${formData.fecha_inicio}_a_${formData.fecha_termino}.xlsx`
      );
      document.body.appendChild(link);
      link.click();

      // Limpiar
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

      alert('✅ Informe generado y descargado exitosamente');
      onClose();
    } catch (err) {
      console.error('Error generando informe:', err);

      // Intentar obtener el mensaje de error del backend
      let errorMessage = 'Error al generar el informe. Por favor intenta nuevamente.';

      if (err.response?.data) {
        // Si el error viene como blob, necesitamos convertirlo a texto
        if (err.response.data instanceof Blob) {
          const text = await err.response.data.text();
          try {
            const jsonError = JSON.parse(text);
            errorMessage = jsonError.error || errorMessage;
          } catch (e) {
            // Si no es JSON, usar el mensaje por defecto
          }
        } else if (err.response.data.error) {
          errorMessage = err.response.data.error;
        }
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const selectedInfluencer = availableInfluencers.find(
    inf => inf.id === parseInt(formData.influencer)
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Generar Informe"
      subtitle="Selecciona los filtros para generar el informe"
      icon="📊"
      size="m"
      footer={
        <>
          <button
            onClick={onClose}
            className="btn-secondary"
            disabled={loading}
          >
            Cancelar
          </button>
          <button
            onClick={handleGenerarInforme}
            className="btn-primary"
            disabled={loading}
          >
            {loading ? 'Generando...' : '📊 Generar Informe'}
          </button>
        </>
      }
    >
      <div className="informe-modal-content">
        {error && (
          <div className="error-message">
            ⚠️ {error}
          </div>
        )}

        {/* Campo Cuentas (Influencer) */}
        <div className="form-group">
          <label htmlFor="influencer">
            Cuentas <span className="required">*</span>
          </label>
          <select
            id="influencer"
            value={formData.influencer}
            onChange={(e) => handleInputChange('influencer', e.target.value)}
            className="form-control"
            disabled={loading}
          >
            <option value="">Seleccionar cuenta...</option>
            {availableInfluencers.map(influencer => (
              <option key={influencer.id} value={influencer.id}>
                {influencer.name}
              </option>
            ))}
          </select>
          {selectedInfluencer && selectedInfluencer.profileImage && (
            <div className="influencer-preview">
              <img
                src={selectedInfluencer.profileImage}
                alt={selectedInfluencer.name}
                className="influencer-avatar"
              />
              <span>{selectedInfluencer.name}</span>
            </div>
          )}
        </div>

        {/* Campo Fecha Inicio */}
        <div className="form-group">
          <label htmlFor="fecha_inicio">
            Fecha de Publicación - Inicio <span className="required">*</span>
          </label>
          <input
            type="date"
            id="fecha_inicio"
            value={formData.fecha_inicio}
            onChange={(e) => handleInputChange('fecha_inicio', e.target.value)}
            className="form-control"
            disabled={loading}
          />
          <small className="form-help">
            Filtra registros desde esta fecha (campo: timestamp)
          </small>
        </div>

        {/* Campo Fecha Término */}
        <div className="form-group">
          <label htmlFor="fecha_termino">
            Fecha de Publicación - Término <span className="required">*</span>
          </label>
          <input
            type="date"
            id="fecha_termino"
            value={formData.fecha_termino}
            onChange={(e) => handleInputChange('fecha_termino', e.target.value)}
            className="form-control"
            disabled={loading}
          />
          <small className="form-help">
            Filtra registros hasta esta fecha (campo: timestamp)
          </small>
        </div>

        {/* Resumen de selección */}
        {formData.influencer && formData.fecha_inicio && formData.fecha_termino && (
          <div className="informe-summary">
            <h4>📋 Resumen del Informe</h4>
            <ul>
              <li>
                <strong>Cuenta:</strong>{' '}
                {availableInfluencers.find(inf => inf.id === parseInt(formData.influencer))?.name}
              </li>
              <li>
                <strong>Período:</strong>{' '}
                {new Date(formData.fecha_inicio).toLocaleDateString('es-ES')} -{' '}
                {new Date(formData.fecha_termino).toLocaleDateString('es-ES')}
              </li>
              <li>
                <strong>Días:</strong>{' '}
                {Math.ceil((new Date(formData.fecha_termino) - new Date(formData.fecha_inicio)) / (1000 * 60 * 60 * 24)) + 1}
              </li>
            </ul>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default InformeModal;
